import React, { useEffect, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';

const ProtectedRoute = ({ children, allowedRoles = [] }) => {
  const { user, isAuthenticated, isLoggingOut, loading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [redirecting, setRedirecting] = useState(false);
  
  useEffect(() => {
    // Don't do anything while auth is still loading
    if (loading) {
      return;
    }

    if (isLoggingOut) {
      return;
    }

    if (!isAuthenticated) {
      // Preserve intended path for post-login redirect
      try {
        const intended = typeof location === 'string' ? location : location?.pathname;
        if (intended) {
          localStorage.setItem('redirectAfterLogin', intended);
        }
      } catch {}
      toast.error('Please sign in to access this page');
      setRedirecting(true);
      navigate('/signin', { state: { from: location }, replace: true });
      return;
    }
    
    if (allowedRoles.length > 0 && !allowedRoles.includes(user?.role)) {
      toast.error('You do not have permission to access this page');
      setRedirecting(true);
      navigate('/', { replace: true });
      return;
    }
  }, [loading, isAuthenticated, user, allowedRoles, navigate, location, isLoggingOut]);
  
  // Show loading while auth is initializing
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // During redirects, show a non-flashy loader instead of rendering nothing
  if (redirecting) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-gray-400 mx-auto"></div>
          <p className="mt-3 text-gray-500">Redirecting…</p>
        </div>
      </div>
    );
  }

  // Only render children if authentication checks pass
  return children;
};

export default ProtectedRoute; 